import { createTheme, ThemeOptions } from '@mui/material/styles';

// Modern iBusiness brand colors with blue-to-teal gradients
const brandColors = {
  primary: {
    main: '#1B365D', // Deep blue
    light: '#4A6CF7', // Updated lighter blue for gradient
    dark: '#0F2340',
    contrastText: '#FFFFFF',
  },
  secondary: {
    main: '#8B5CF6', // Modern purple accent
    light: '#A78BFA',
    dark: '#7C3AED',
    contrastText: '#FFFFFF',
  },
  success: {
    main: '#10B981',
    light: '#34D399',
    dark: '#059669',
  },
  warning: {
    main: '#F59E0B',
    light: '#FBBF24',
    dark: '#D97706',
  },
  error: {
    main: '#EF4444',
    light: '#F87171',
    dark: '#DC2626',
  },
  info: {
    main: '#3B82F6',
    light: '#60A5FA',
    dark: '#2563EB',
  },
  grey: {
    50: '#F9FAFB',
    100: '#F3F4F6',
    200: '#E5E7EB',
    300: '#D1D5DB',
    400: '#9CA3AF',
    500: '#6B7280',
    600: '#4B5563',
    700: '#374151',
    800: '#1F2937',
    900: '#111827',
  },
  background: {
    default: '#F9FAFB',
    paper: '#FFFFFF',
  },
  text: {
    primary: '#1F2937', // Improved contrast for accessibility
    secondary: '#6B7280',
    disabled: '#9CA3AF',
  },
};

// Modern gradient definitions
const gradients = {
  primary: 'linear-gradient(27deg, #166cb1 39%, #11aae2 73%, #12d9b8)',
  primarySubtle: 'linear-gradient(27deg, #166cb1 39%, #11aae2 73%, #12d9b8)',
  secondary: 'linear-gradient(27deg, #166cb1 39%, #11aae2 73%, #12d9b8)',
  background: 'linear-gradient(27deg, #166cb1 39%, #11aae2 73%, #12d9b8)',
  card: 'linear-gradient(145deg, #FFFFFF 0%, #F8FAFC 100%)',
};

const themeOptions: ThemeOptions = {
  palette: {
    mode: 'light',
    ...brandColors,
  },
  typography: {
    fontFamily: "'Open Sans', Arial, Helvetica, sans-serif",
    h1: {
      fontSize: '2.5rem',
      fontWeight: 600,
      lineHeight: 1.2,
      letterSpacing: '-0.01562em',
    },
    h2: {
      fontSize: '2rem',
      fontWeight: 600,
      lineHeight: 1.3,
      letterSpacing: '-0.00833em',
    },
    h3: {
      fontSize: '1.75rem',
      fontWeight: 600,
      lineHeight: 1.4,
      letterSpacing: '0em',
    },
    h4: {
      fontSize: '1.5rem',
      fontWeight: 600,
      lineHeight: 1.4,
      letterSpacing: '0.00735em',
    },
    h5: {
      fontSize: '1.25rem',
      fontWeight: 600,
      lineHeight: 1.5,
      letterSpacing: '0em',
    },
    h6: {
      fontSize: '1.125rem',
      fontWeight: 600,
      lineHeight: 1.5,
      letterSpacing: '0.0075em',
    },
    body1: {
      fontSize: '1rem',
      lineHeight: 1.6,
      letterSpacing: '0.00938em',
    },
    body2: {
      fontSize: '0.875rem',
      lineHeight: 1.6,
      letterSpacing: '0.01071em',
    },
    button: {
      fontSize: '0.875rem',
      fontWeight: 600,
      letterSpacing: '0.02857em',
      textTransform: 'none' as const,
    },
  },
  spacing: 4,
  shape: {
    borderRadius: 4,
  },
  components: {
    MuiListItemIcon: {
      styleOverrides: {
        root: {
          minWidth: "48px",
        },
      }
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          textTransform: 'none',
          fontWeight: 600,
          padding: '10px 24px',
          boxShadow: 'none',
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            boxShadow: '0 4px 12px rgba(75, 108, 247, 0.25)',
            transform: 'translateY(-1px)',
          },
        },
        containedPrimary: {
          background: gradients.secondary,
          color: '#FFFFFF',
          '&:hover': {
            background: gradients.secondary,
            boxShadow: '0 6px 20px rgba(75, 108, 247, 0.4)',
          },
          '&:active': {
            transform: 'translateY(0px)',
          },
          '&.Mui-disabled': {
            background: 'rgba(75, 108, 247, 0.12)',
            color: 'rgba(55, 65, 81, 0.7)', // Dark gray for better readability but clearly disabled
          },
        },
        containedSecondary: {
          backgroundColor: brandColors.grey[100],
          color: brandColors.text.primary,
          '&:hover': {
            backgroundColor: brandColors.grey[200],
            boxShadow: '0 4px 12px rgba(107, 114, 128, 0.15)',
          },
        },
        outlined: {
          borderColor: brandColors.grey[300],
          color: brandColors.text.primary,
          '&:hover': {
            borderColor: brandColors.primary.light,
            backgroundColor: 'rgba(75, 108, 247, 0.04)',
          },
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          margin: '2px 8px',
          '&.Mui-selected': {
            background: gradients.secondary,
            color: 'white',
            '&:hover': {
              background: gradients.secondary,
            },
            '& .MuiListItemIcon-root': {
              color: 'white',
            },
          },
          '&:hover': {
            backgroundColor: 'rgba(75, 108, 247, 0.08)',
            borderRadius: 8,
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          border: '1px solid rgba(229, 231, 235, 0.8)',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
          background: gradients.card,
          '&:hover': {
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 8,
        },
        elevation1: {
          border: '1px solid rgba(229, 231, 235, 0.8)',
          boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 8,
            '& fieldset': {
              borderColor: brandColors.grey[300],
            },
            '&:hover fieldset': {
              borderColor: brandColors.primary.light,
            },
            '&.Mui-focused fieldset': {
              borderColor: brandColors.primary.light,
              borderWidth: 2,
              boxShadow: `0 0 0 3px rgba(75, 108, 247, 0.1)`,
            },
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          fontWeight: 500,
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          color: brandColors.text.primary,
          backgroundColor: brandColors.background.paper,
          borderRadius: 0,
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
          backdropFilter: 'blur(8px)',
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          backgroundColor: brandColors.grey[50],
          '& .MuiTableCell-head': {
            fontWeight: 600,
            color: brandColors.text.primary,
          },
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
          fontSize: '1rem',
        },
      },
    },
  },
};

export const theme = createTheme(themeOptions);

// Export gradients for use in components
export { gradients };

// Custom theme variants for different contexts
export const createCustomTheme = (mode: 'light' | 'dark' = 'light') => {
  return createTheme({
    ...themeOptions,
    palette: {
      mode,
      ...(mode === 'dark' ? {
        primary: {
          main: '#4A90E2',
          light: '#6AA8F5',
          dark: '#2E6CB3',
          contrastText: '#FFFFFF',
        },
        background: {
          default: '#121212',
          paper: '#1E1E1E',
        },
        text: {
          primary: '#FFFFFF',
          secondary: '#B0B0B0',
        },
      } : brandColors),
    },
  });
};

export default theme;